import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { AppLayout } from './components/AppLayout.jsx';
import { ProtectedRoute } from './components/ProtectedRoute.jsx';
import { RouteFallback } from './components/RouteFallback.jsx';
import { useSessionStore } from './lib/session.js';

// Auth chunk
const LoginPage = lazy(() => import('./features/auth/pages/LoginPage.jsx'));
const TwoFactorPage = lazy(() => import('./features/auth/pages/TwoFactorPage.jsx'));
const SignupDoctorPage = lazy(() => import('./features/auth/pages/SignupDoctorPage.jsx'));
const SignupPharmacyPage = lazy(() => import('./features/auth/pages/SignupPharmacyPage.jsx'));

// Doctor chunk
const DoctorDashboard = lazy(() => import('./features/doctor/pages/DoctorDashboard.jsx'));
const NewPrescriptionPage = lazy(() => import('./features/doctor/pages/NewPrescriptionPage.jsx'));
const PrescriptionListPage = lazy(() => import('./features/doctor/pages/PrescriptionListPage.jsx'));
const PrescriptionSuccessPage = lazy(
  () => import('./features/doctor/pages/PrescriptionSuccessPage.jsx'),
);

// Pharmacy chunk
const PharmacyScanPage = lazy(() => import('./features/pharmacy/pages/PharmacyScanPage.jsx'));
const PharmacyHistoryPage = lazy(() => import('./features/pharmacy/pages/PharmacyHistoryPage.jsx'));

// Patient chunk (public link — kept tiny + standalone)
const PatientViewPage = lazy(() => import('./features/patient/pages/PatientViewPage.jsx'));

// Admin chunk
const AdminDoctorsPage = lazy(() => import('./features/admin/pages/AdminDoctorsPage.jsx'));

// Misc
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.jsx'));

function HomeRedirect() {
  const { user } = useSessionStore();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'DOCTOR') return <Navigate to="/doctor" replace />;
  if (user.role === 'PHARMACY') return <Navigate to="/pharmacy" replace />;
  if (user.role === 'ADMIN') return <Navigate to="/admin/doctors" replace />;
  return <Navigate to="/login" replace />;
}

export default function App() {
  const hydrate = useSessionStore((s) => s.hydrate);
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<TwoFactorPage />} />
        <Route path="/signup/doctor" element={<SignupDoctorPage />} />
        <Route path="/signup/pharmacy" element={<SignupPharmacyPage />} />
        <Route path="/p/:shortCode" element={<PatientViewPage />} />

        <Route
          element={
            <ProtectedRoute roles={['DOCTOR']}>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/doctor" element={<DoctorDashboard />} />
          <Route path="/doctor/new" element={<NewPrescriptionPage />} />
          <Route path="/doctor/list" element={<PrescriptionListPage />} />
          <Route path="/doctor/success/:id" element={<PrescriptionSuccessPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute roles={['PHARMACY']}>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/pharmacy" element={<PharmacyScanPage />} />
          <Route path="/pharmacy/history" element={<PharmacyHistoryPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute roles={['ADMIN']}>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin/doctors" element={<AdminDoctorsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
